import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Plus, X } from "lucide-react";
import type { StickerDef } from "@/types";

export function StickerPanel({
  stickers,
  onStickerSelect,
  onUpload,
  onUploadUrl,
  onDelete,
}: {
  stickers: StickerDef[];
  onStickerSelect: (s: StickerDef) => void;
  onUpload: (file: File) => Promise<void>;
  onUploadUrl: (url: string) => Promise<void>;
  onDelete: (id: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try { await onUpload(file); } finally { setUploading(false); }
  };

  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [ctxMenu]);

  const handlePaste = async () => {
    setCtxMenu(null);
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imgType = item.types.find((t) => t.startsWith("image/"));
        if (imgType) {
          const blob = await item.getType(imgType);
          const ext = imgType.split("/")[1] || "png";
          const file = new File([blob], `pasted.${ext}`, { type: imgType });
          await handleUpload(file);
          return;
        }
      }
    } catch {}
    try {
      const text = (await navigator.clipboard.readText()).trim();
      if (text.startsWith("http://") || text.startsWith("https://")) {
        setUploading(true);
        try { await onUploadUrl(text); } finally { setUploading(false); }
      }
    } catch (err) {
      console.error("Paste failed:", err);
    }
  };

  return (
    <div
      style={{
        position: "absolute",
        bottom: "calc(100% + 8px)",
        left: "50%",
        transform: "translateX(-50%)",
        background: "var(--card)",
        border: `1px solid ${isDragOver ? "#0091ff" : "var(--border)"}`,
        borderRadius: 12,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)",
        padding: 12,
        width: 256,
        transition: "border-color 0.15s",
        pointerEvents: "auto",
      }}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setCtxMenu({ x: e.clientX, y: e.clientY }); }}
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false);
      }}
      onDrop={async (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file?.type.startsWith("image/")) await handleUpload(file);
      }}
    >
      <div style={{
        fontSize: 10,
        fontWeight: 600,
        color: "var(--muted-foreground)",
        marginBottom: 8,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
      }}>
        Stickers
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
        {stickers.map((s) => (
          <div key={s._id} style={{ position: "relative" }}>
            <div
              style={{
                aspectRatio: "1",
                borderRadius: 8,
                overflow: "hidden",
                cursor: "pointer",
                border: "1px solid var(--border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "var(--muted)",
              }}
              onClick={() => onStickerSelect(s)}
              title={s.name}
            >
              <img
                src={`/api/sticker-images/${s._id}`}
                style={{ width: "85%", height: "85%", objectFit: "contain" }}
                draggable={false}
                alt={s.name}
              />
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(s._id); }}
              style={{
                position: "absolute",
                top: -4,
                right: -4,
                width: 16,
                height: 16,
                borderRadius: "50%",
                border: "none",
                background: "var(--foreground)",
                color: "var(--background)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                padding: 0,
              }}
              title="Remove sticker"
            >
              <X size={10} />
            </button>
          </div>
        ))}

        {/* Upload slot */}
        <div
          style={{
            aspectRatio: "1",
            borderRadius: 8,
            border: `1.5px dashed ${isDragOver ? "#0091ff" : "var(--border)"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: uploading ? "wait" : "pointer",
            color: isDragOver ? "#0091ff" : "var(--muted-foreground)",
            transition: "border-color 0.15s, color 0.15s",
          }}
          onClick={() => !uploading && fileInputRef.current?.click()}
          title="Upload sticker"
        >
          {uploading ? (
            <div style={{
              width: 14,
              height: 14,
              border: "2px solid currentColor",
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin 0.6s linear infinite",
            }} />
          ) : (
            <Plus size={18} />
          )}
        </div>
      </div>

      {stickers.length === 0 && !isDragOver && (
        <p style={{ margin: "8px 0 0", fontSize: 11, color: "var(--muted-foreground)", textAlign: "center" }}>
          Drop images here or click + to add
        </p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) await handleUpload(file);
          e.target.value = "";
        }}
      />

      {ctxMenu && createPortal(
        <div
          style={{
            position: "fixed",
            left: ctxMenu.x,
            top: ctxMenu.y,
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.14)",
            padding: 4,
            zIndex: 9999,
            minWidth: 140,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            style={{
              display: "block",
              width: "100%",
              padding: "6px 12px",
              background: "transparent",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              textAlign: "left",
              fontSize: 13,
              color: "var(--foreground)",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--muted)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
            onClick={handlePaste}
          >
            Paste Image
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
